'use client';

import type { Event_Entry } from '@/types/graphql';
import { cn } from '@/utils/cn';
import { useRouter } from 'next/navigation';
import { usePathname } from 'next/navigation';
import { Children, type ReactElement, cloneElement, useEffect, useState } from 'react';

interface FilteredShowsProps {
    children: ReactElement<{
        shows: Event_Entry[];
        locale: string;
        activeFilters?: string[];
        filterAction?: (filter: string) => void;
    }>;
    withRouter: boolean;
    className?: string;
}

export const FilteredShows = ({ children, withRouter, className }: FilteredShowsProps) => {
    const [activeFilters, setActiveFilters] = useState<string[]>([]);
    const router = useRouter();
    const pathname = usePathname();

    const filterAction = (value: string) => {
        setActiveFilters((prev) => {
            // If the clicked filter is already active, clear all filters
            if (prev.includes(value)) {
                const newFilters: string[] = [];

                if (withRouter) {
                    router.push(pathname, { scroll: false });
                }

                return newFilters;
            }

            // Otherwise, set only this filter as active
            const newFilters = [value];

            if (withRouter) {
                router.push(`${pathname}#${value}`, { scroll: false });
            }

            return newFilters;
        });
    };

    // Process hash in URL on load
    useEffect(() => {
        if (typeof window === 'undefined') return;

        const hash = window.location.hash;
        if (hash) {
            // Only take the first filter from the hash
            const filter = hash.substring(1);
            setActiveFilters([filter]);
        } else {
            setActiveFilters([]);
        }
    }, []);

    return (
        <div className={cn(className)}>
            {Children.map(children, (child) => {
                const extraProps = {
                    activeFilters,
                    filterAction,
                };
                const newProps = { ...child.props, ...extraProps };
                return cloneElement(child, newProps);
            })}
        </div>
    );
};
